import { useQueryClient } from '@tanstack/react-query';

import { invalidateOnFarmImplantSession } from '@/services/on-farm-implant';
import { useEventSource } from '@/utils/event-source';

type SessionEventType =
  | 'READY'
  | 'PING'
  | 'TOKEN_EXPIRED'
  | 'SESSION_CHANGED'
  | 'SOURCES_CHANGED'
  | 'EMBRYOS_CHANGED'
  | 'QUEUE_CHANGED'
  | 'LOCK_CHANGED'
  | 'QC_CHANGED';

type SessionEvent = { type: SessionEventType };

const INVALIDATES: Partial<Record<SessionEventType, readonly string[]>> = {
  SESSION_CHANGED: ['session', 'queue', 'qc'],
  SOURCES_CHANGED: [
    'session',
    'selection',
    'session-embryos',
    'fresh-matings',
    'fresh-embryos',
    'qc',
  ],
  EMBRYOS_CHANGED: [
    'session',
    'session-embryos',
    'fresh-embryos',
    'fresh-matings',
    'queue',
    'selection',
    'on-farm-freezing',
    'qc',
  ],
  QUEUE_CHANGED: ['queue', 'session-embryos'],
  LOCK_CHANGED: ['fresh-embryos'],
  QC_CHANGED: ['qc'],
};

export function useSessionEvents(sessionId: string | undefined) {
  const queryClient = useQueryClient();

  useEventSource<SessionEvent, { sessionId: string }>({
    path: `/api/on-farm-implant/sessions/${sessionId}/events`,
    meta: { sessionId: sessionId ?? '' },
    enabled: !!sessionId,
    onReconnect({ meta }) {
      void invalidateOnFarmImplantSession(queryClient, meta.sessionId);
    },
    onMessage(event, { meta }) {
      for (const kind of INVALIDATES[event.type] ?? []) {
        void queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', kind, meta.sessionId],
        });
      }
    },
  });
}
